/**
 * Authorization catalogue.
 *
 * The legacy system had an empty `user-roles` table and 496 of 505 users with
 * a NULL role: every authenticated call was effectively an admin call. Here the
 * unit of authorization is the permission, roles are just named bundles, and
 * both live in the database so they can be audited and changed without a deploy.
 *
 * Naming: `<resource>:<action>[:<scope>]` where scope is `own` | `team` | `all`.
 */
import { RoleCode } from './enums.js';
export const PERMISSIONS = {
    // --- identity & organisation
    USERS_READ: 'users:read',
    /**
     * Aprire la **rubrica**: l'elenco dei dipendenti e le loro schede, nel Cruscotto.
     *
     * ⚠ **Non è `USERS_READ`, ed è nato apposta per non esserlo.** `users:read` ce l'hanno tutti
     * dal 2026-10-03 — decisione di Cosimo, «sì, a tutti i dipendenti», perché convocare una
     * riunione comincia con lo scegliere chi invitare — e quindi non può più dire «questa pagina
     * è tua». Il 14 ottobre Cosimo ha chiesto di togliere la pagina ai dipendenti e agli
     * approvatori: il menù lo aveva già fatto, l'indirizzo scritto a mano no.
     *
     * Ce l'hanno `hr`, `admin` e **`reception`** — che l'anagrafica la usa da agosto, col recinto
     * per sede fatto apposta. Chiudere la pagina su `users:write` l'avrebbe tagliata fuori, e
     * allargarle `users:write` avrebbe aperto ogni recinto che quel permesso legge per dire
     * un'altra cosa.
     */
    USERS_DIRECTORY: 'users:directory',
    USERS_WRITE: 'users:write',
    USERS_DEACTIVATE: 'users:deactivate',
    USERS_RESET_PASSWORD: 'users:reset-password',
    /**
     * L'archivio dei cessati. Chi non ce l'ha non vede più chi se n'è andato: né in elenco,
     * né aprendone la scheda. Chi ce l'ha li vede tutti, e con tutto quello che c'era —
     * perché la scelta è stata «archivio dedicato», non «anonimizzazione» (K88).
     */
    ARCHIVE_TERMINATED_READ: 'archive:terminated:read',
    ROLES_READ: 'roles:read',
    ROLES_WRITE: 'roles:write',
    DEPARTMENTS_READ: 'departments:read',
    DEPARTMENTS_WRITE: 'departments:write',
    /**
     * I **turni di tutti**, in sola visione.
     *
     * Nei turni l'autorita' di costruire e' un'**appartenenza** (`DepartmentManager`): la
     * settimana la fa chi guida quel reparto. Questo permesso non la tocca - apre soltanto la
     * porta a **guardare** le griglie di ogni reparto, che e' quel che serve all'ufficio del
     * personale per sapere chi lavora quando, senza mettere le mani nel lavoro dei responsabili.
     *
     * ⚠ Nasce il 2026-10-08 da una richiesta di Cosimo, e nasce **separando**: fino a quel
     * giorno i turni si aprivano con `departments:write`, che pero' e' un permesso di
     * **scrittura** - quindi l'ufficio del personale non li vedeva soltanto, li poteva anche
     * rifare. «Sola visione» non si poteva esprimere, e un permesso che non sa dire «solo
     * guardare» finisce per dare troppo a chi chiedeva poco.
     */
    SHIFTS_READ_ALL: 'shifts:read:all',
    /**
     * Costruire i turni di **qualunque** reparto, senza esserne responsabile.
     *
     * E' il pezzo di scrittura che prima viaggiava dentro `departments:write`. Separarlo serve a
     * poter dare la sola visione senza dare anche il pennello: adesso il confine fra «guardo» e
     * «rifaccio» e' una cosa che si puo' scrivere.
     */
    SHIFTS_MANAGE_ALL: 'shifts:manage:all',
    HEADQUARTERS_READ: 'headquarters:read',
    HEADQUARTERS_WRITE: 'headquarters:write',
    /**
     * Costruire le **linee di produzione**: crearle, metterci dentro la gente, dire chi le
     * sorveglia.
     *
     * Non c'è il permesso di *guardare* una linea, ed è voluto: guardare non dipende da un
     * diritto del ruolo ma da un'**appartenenza** — si vede la propria linea perché si è scritti
     * fra chi la sorveglia, come i responsabili di reparto e come la sede della reception. Un
     * permesso in più direbbe «può guardare le linee» senza dire quali, e quali è tutto.
     */
    PRODUCTION_LINES_MANAGE: 'production-lines:manage',
    // --- attendance
    WORKTIMES_READ_OWN: 'worktimes:read:own',
    WORKTIMES_WRITE_OWN: 'worktimes:write:own',
    WORKTIMES_READ_TEAM: 'worktimes:read:team',
    WORKTIMES_READ_ALL: 'worktimes:read:all',
    WORKTIMES_CORRECT: 'worktimes:correct',
    // --- leave
    JUSTIFICATIONS_READ_OWN: 'justifications:read:own',
    JUSTIFICATIONS_WRITE_OWN: 'justifications:write:own',
    JUSTIFICATIONS_READ_TEAM: 'justifications:read:team',
    JUSTIFICATIONS_READ_ALL: 'justifications:read:all',
    JUSTIFICATIONS_APPROVE_L1: 'justifications:approve:l1',
    JUSTIFICATIONS_APPROVE_L2: 'justifications:approve:l2',
    JUSTIFICATIONS_MANAGE: 'justifications:manage',
    PROPOSALS_RESPOND: 'proposals:respond',
    PROPOSALS_MANAGE: 'proposals:manage',
    LEAVE_BALANCES_READ_OWN: 'leave-balances:read:own',
    LEAVE_BALANCES_MANAGE: 'leave-balances:manage',
    // --- payroll
    PAYCHECKS_READ_OWN: 'paychecks:read:own',
    PAYCHECKS_MANAGE: 'paychecks:manage',
    // --- expenses
    EXPENSES_READ_OWN: 'expenses:read:own',
    EXPENSES_WRITE_OWN: 'expenses:write:own',
    EXPENSES_REVIEW: 'expenses:review',
    EXPENSES_MANAGE: 'expenses:manage',
    BUSINESS_TRIPS_READ_OWN: 'business-trips:read:own',
    BUSINESS_TRIPS_WRITE_OWN: 'business-trips:write:own',
    BUSINESS_TRIPS_MANAGE: 'business-trips:manage',
    // --- facilities & fleet
    VEHICLES_READ: 'vehicles:read',
    VEHICLES_BOOK: 'vehicles:book',
    VEHICLES_MANAGE: 'vehicles:manage',
    MEETING_ROOMS_READ: 'meeting-rooms:read',
    MEETING_ROOMS_BOOK: 'meeting-rooms:book',
    MEETING_ROOMS_MANAGE: 'meeting-rooms:manage',
    CALENDAR_READ_OWN: 'calendar:read:own',
    CALENDAR_READ_ALL: 'calendar:read:all',
    CALENDAR_WRITE: 'calendar:write',
    // --- uniforms
    UNIFORMS_REQUEST: 'uniforms:request',
    UNIFORMS_MANAGE: 'uniforms:manage',
    /// La mensa: ordinare per sé, e vedere il proprio storico.
    CANTEEN_ORDER_OWN: 'canteen:order:own',
    /// La mensa dall'altra parte: caricare il menù, accendere le sedi, vedere il quadro
    /// del giorno e chi ha ordinato. È il mestiere della reception, e non si eredita da
    /// chi ordina — sono due lavori diversi (`spec/mensa.md` §8).
    CANTEEN_MANAGE: 'canteen:manage',
    /// La mensa dal lato dei soldi: i listini, il prospetto del mese, quanto ha mangiato una
    /// persona.
    ///
    /// Sta separato da `canteen:manage` perché sono due gesti diversi — caricare il menù non è
    /// contare i soldi — e a chi va lo ha deciso **Cosimo, il 2026-09-30**: «questa funzione deve
    /// essere assegnata al reparto HR» e «e agli account dei centralini». Quindi Risorse Umane
    /// **e** reception, che sono i due posti da cui si apriva il programmino sulla share.
    ///
    /// *(Questa riga diceva il contrario — «non alla reception, un prospetto nominativo di spese
    /// non va davanti a sei banchi» — ed era una mia proposta, non una decisione sua. Resta
    /// scritta perché la conseguenza che nominava è vera e va saputa: vedi il monito su
    /// `RECEPTION_EXTRA`.)*
    CANTEEN_BILLING: 'canteen:billing',
    // --- communication
    CHAT_USE: 'chat:use',
    CHAT_MODERATE: 'chat:moderate',
    NOTIFICATIONS_READ_OWN: 'notifications:read:own',
    /**
     * Scrivere una comunicazione, e a chi può arrivare.
     *
     * Fino al 2026-09-06 era un permesso solo, `notifications:send`, **senza scope**: chi ce
     * l'aveva scriveva a chiunque, e `everyone: true` svegliava cinquecento telefoni. Era
     * l'unico punto del sistema in cui lo scoping team/all — attento dappertutto — mancava.
     * Cosimo l'ha deciso il 2026-09-06: «le circolari: HR e Amministratori a tutti; ogni
     * Responsabile a tutto il proprio reparto».
     *
     * Due permessi e non un flag, con l'idioma già in casa (`WORKTIMES_READ_TEAM`/`_ALL`,
     * `JUSTIFICATIONS_READ_TEAM`/`_ALL`): la porta è `_TEAM`, e `_ALL` **non è una seconda
     * porta, è un allargamento**. Nessuna rotta chiede `_ALL` per lasciar passare qualcuno che
     * `_TEAM` non ha già lasciato entrare, esattamente come le timbrature: chi vede tutto vede
     * anche la propria squadra, e nei pacchetti di ruolo `_ALL` non viaggia mai da solo
     * (`HR_EXTRA` parte da `MANAGER_EXTRA`). Chi spuntasse solo `_ALL` dal pannello dei ruoli
     * si ritroverebbe fermo alla porta, e il rifiuto della guardia gli direbbe quale casella
     * manca — che è il motivo per cui quella frase nomina il permesso.
     *
     * Il **come** lo fa il server: `NotificationsService.resolveAudience` riceve chi sta
     * chiedendo e restringe la platea a `subjectsOf` — la stessa definizione di «i miei» che
     * riempie la coda delle firme, così menù, coda e circolari non divergono mai. Il client non
     * c'entra: qualunque cosa mandi, fuori da lì non si esce (lezione di K196).
     */
    NOTIFICATIONS_SEND_TEAM: 'notifications:send:team',
    NOTIFICATIONS_SEND_ALL: 'notifications:send:all',
    CONTACTS_READ: 'contacts:read',
    CONTACTS_MANAGE: 'contacts:manage',
    // --- logistics
    LOGISTICS_LOCATIONS_READ: 'logistics:locations:read',
    /*
      ⚠ **`create` e `manage` sono due domande diverse, e per undici giorni sono stati la
      stessa.** Il 2026-10-03 `manage` è stato dato a tutti perché l'autista possa **creare**
      il luogo che gli serve mentre è in viaggio (la riga di Cosimo sta su `EMPLOYEE_BASE`).
      `manage` però comprende anche rinominare, fondere, disattivare e riattivare un luogo
      **altrui**: misurato il 2026-10-14, **445 persone** potevano rinominare o spegnere
      qualunque dei **259** luoghi dell'azienda, che è esattamente K519 con un altro nome.
      Cosimo, 2026-10-14: *«Spezzare in due permessi»*.
    */
    LOGISTICS_LOCATIONS_CREATE: 'logistics:locations:create',
    LOGISTICS_LOCATIONS_MANAGE: 'logistics:locations:manage',
    LOGISTICS_PACKAGE_TYPES_MANAGE: 'logistics:package-types:manage',
    LOGISTICS_TRANSFERS_READ: 'logistics:transfers:read',
    LOGISTICS_TRANSFERS_MANAGE: 'logistics:transfers:manage',
    LOGISTICS_TRIPS_READ: 'logistics:trips:read',
    LOGISTICS_TRIPS_MANAGE: 'logistics:trips:manage',
    LOGISTICS_TRIPS_DRIVE: 'logistics:trips:drive',
    // --- platform
    SETTINGS_READ: 'settings:read',
    SETTINGS_MANAGE: 'settings:manage',
    AUDIT_READ: 'audit:read',
    /**
     * Le righe di autenticazione del registro — `LOGIN`, `LOGIN_FAILED`, `LOGOUT` — separate
     * dal resto (sweep legale, R4b).
     *
     * Il registro serve a ricostruire chi ha approvato, chi ha scaricato, chi ha revocato:
     * fatti amministrativi, di cui esiste una pratica. Le righe di accesso non sono quello.
     * Messe in fila per una persona sola raccontano un'altra storia — a che ora apre l'app
     * ogni mattina, da quale rete, con quale telefono, quante volte ha sbagliato la password
     * il lunedì dopo le ferie — e la raccontano per sempre, perché il registro non si cancella.
     * Nessuno ha deciso di tenere un diario delle abitudini di ogni dipendente: è venuto fuori
     * da solo, come effetto di due scelte giuste prese separatamente.
     *
     * Non si toglie il dato, che serve per la sicurezza (un accesso da una rete che non è la
     * nostra, cento tentativi falliti di notte): si stringe la platea. Chi amministra il
     * sistema lo vede perché è il suo mestiere; HR vede il registro per le proprie pratiche,
     * e le pratiche non sono l'orario di apertura dell'app dei colleghi.
     */
    AUDIT_READ_AUTH: 'audit:read:auth',
    JOBS_READ: 'jobs:read',
    REPORTS_EXPORT: 'reports:export',
    /**
     * Il centralino di Beste Talk (2026-11-02): gli interni, chi ha l'app, le chiamate, i telefoni
     * che non squillano. Non sta in nessun ruolo: l'ha l'amministratore (che ha tutto) e chi ha
     * l'interruttore «Gestisce il centralino» sull'account (`PERMESSI_DI_CHI_GESTISCE_IL_CENTRALINO`).
     */
    TELEPHONY_MANAGE: 'telephony:manage',
};
/** Human labels + UI grouping, used by the seed and by the roles admin screen. */
export const PERMISSION_META = {
    [PERMISSIONS.USERS_READ]: { name: 'Vedere anagrafiche', group: 'Organizzazione' },
    [PERMISSIONS.USERS_DIRECTORY]: {
        name: 'Aprire la rubrica dei dipendenti (elenco e schede)',
        group: 'Organizzazione',
    },
    [PERMISSIONS.USERS_WRITE]: { name: 'Modificare anagrafiche', group: 'Organizzazione' },
    [PERMISSIONS.USERS_DEACTIVATE]: { name: 'Disattivare utenti', group: 'Organizzazione' },
    [PERMISSIONS.USERS_RESET_PASSWORD]: { name: 'Reimpostare password', group: 'Organizzazione' },
    [PERMISSIONS.ARCHIVE_TERMINATED_READ]: {
        name: 'Consultare l’archivio dei cessati',
        group: 'Organizzazione',
    },
    [PERMISSIONS.ROLES_READ]: { name: 'Vedere ruoli', group: 'Organizzazione' },
    [PERMISSIONS.ROLES_WRITE]: { name: 'Modificare ruoli e permessi', group: 'Organizzazione' },
    [PERMISSIONS.DEPARTMENTS_READ]: { name: 'Vedere reparti', group: 'Organizzazione' },
    [PERMISSIONS.DEPARTMENTS_WRITE]: { name: 'Modificare reparti', group: 'Organizzazione' },
    [PERMISSIONS.SHIFTS_READ_ALL]: {
        name: 'Vedere i turni di tutti (sola visione)',
        group: 'Organizzazione',
    },
    [PERMISSIONS.SHIFTS_MANAGE_ALL]: {
        name: 'Costruire i turni di qualunque reparto',
        group: 'Organizzazione',
    },
    [PERMISSIONS.HEADQUARTERS_READ]: { name: 'Vedere sedi', group: 'Organizzazione' },
    [PERMISSIONS.HEADQUARTERS_WRITE]: { name: 'Modificare sedi', group: 'Organizzazione' },
    [PERMISSIONS.PRODUCTION_LINES_MANAGE]: {
        name: 'Gestire le linee di produzione',
        group: 'Organizzazione',
    },
    // Dal 2026-10-03 apre **solo lo stato** («sei dentro da…»), non lo storico: il perché sta in
    // `apps/api/src/worktimes/chi-vede-le-timbrature.ts`. Il nome lo dice, se no promette troppo.
    [PERMISSIONS.WORKTIMES_READ_OWN]: { name: 'Sapere se è dentro o fuori', group: 'Presenze' },
    [PERMISSIONS.WORKTIMES_WRITE_OWN]: { name: 'Timbrare', group: 'Presenze' },
    [PERMISSIONS.WORKTIMES_READ_TEAM]: { name: 'Vedere le timbrature del team', group: 'Presenze' },
    [PERMISSIONS.WORKTIMES_READ_ALL]: { name: 'Vedere tutte le timbrature', group: 'Presenze' },
    [PERMISSIONS.WORKTIMES_CORRECT]: { name: 'Correggere timbrature', group: 'Presenze' },
    [PERMISSIONS.JUSTIFICATIONS_READ_OWN]: { name: 'Vedere le proprie assenze', group: 'Assenze' },
    [PERMISSIONS.JUSTIFICATIONS_WRITE_OWN]: { name: 'Richiedere assenze', group: 'Assenze' },
    [PERMISSIONS.JUSTIFICATIONS_READ_TEAM]: { name: 'Vedere le assenze del team', group: 'Assenze' },
    [PERMISSIONS.JUSTIFICATIONS_READ_ALL]: { name: 'Vedere tutte le assenze', group: 'Assenze' },
    [PERMISSIONS.JUSTIFICATIONS_APPROVE_L1]: { name: 'Approvare (1° livello)', group: 'Assenze' },
    [PERMISSIONS.JUSTIFICATIONS_APPROVE_L2]: { name: 'Approvare (2° livello)', group: 'Assenze' },
    [PERMISSIONS.JUSTIFICATIONS_MANAGE]: { name: 'Gestire tipi e regole assenze', group: 'Assenze' },
    [PERMISSIONS.PROPOSALS_RESPOND]: { name: 'Rispondere a proposte ferie', group: 'Assenze' },
    [PERMISSIONS.PROPOSALS_MANAGE]: { name: 'Proporre periodi di ferie', group: 'Assenze' },
    [PERMISSIONS.LEAVE_BALANCES_READ_OWN]: { name: 'Vedere il proprio residuo', group: 'Assenze' },
    [PERMISSIONS.LEAVE_BALANCES_MANAGE]: { name: 'Gestire i monteore', group: 'Assenze' },
    [PERMISSIONS.PAYCHECKS_READ_OWN]: { name: 'Vedere le proprie buste paga', group: 'Paghe' },
    [PERMISSIONS.PAYCHECKS_MANAGE]: { name: 'Caricare e gestire buste paga', group: 'Paghe' },
    [PERMISSIONS.EXPENSES_READ_OWN]: { name: 'Vedere le proprie note spese', group: 'Spese' },
    [PERMISSIONS.EXPENSES_WRITE_OWN]: { name: 'Inserire note spese', group: 'Spese' },
    [PERMISSIONS.EXPENSES_REVIEW]: { name: 'Approvare note spese', group: 'Spese' },
    [PERMISSIONS.EXPENSES_MANAGE]: { name: 'Gestire e rimborsare note spese', group: 'Spese' },
    [PERMISSIONS.BUSINESS_TRIPS_READ_OWN]: { name: 'Vedere le proprie trasferte', group: 'Spese' },
    [PERMISSIONS.BUSINESS_TRIPS_WRITE_OWN]: { name: 'Inserire trasferte', group: 'Spese' },
    [PERMISSIONS.BUSINESS_TRIPS_MANAGE]: { name: 'Gestire tutte le trasferte', group: 'Spese' },
    [PERMISSIONS.VEHICLES_READ]: { name: 'Vedere i mezzi', group: 'Mezzi e sale' },
    [PERMISSIONS.VEHICLES_BOOK]: { name: 'Prenotare un mezzo', group: 'Mezzi e sale' },
    [PERMISSIONS.VEHICLES_MANAGE]: { name: 'Gestire il parco mezzi', group: 'Mezzi e sale' },
    [PERMISSIONS.MEETING_ROOMS_READ]: { name: 'Vedere le sale', group: 'Mezzi e sale' },
    [PERMISSIONS.MEETING_ROOMS_BOOK]: { name: 'Prenotare una sala', group: 'Mezzi e sale' },
    [PERMISSIONS.MEETING_ROOMS_MANAGE]: { name: 'Gestire le sale', group: 'Mezzi e sale' },
    [PERMISSIONS.CALENDAR_READ_OWN]: { name: 'Vedere il proprio calendario', group: 'Calendario' },
    [PERMISSIONS.CALENDAR_READ_ALL]: { name: 'Vedere il calendario aziendale', group: 'Calendario' },
    [PERMISSIONS.CALENDAR_WRITE]: { name: 'Creare eventi', group: 'Calendario' },
    [PERMISSIONS.UNIFORMS_REQUEST]: { name: 'Richiedere divise', group: 'Divise' },
    [PERMISSIONS.UNIFORMS_MANAGE]: { name: 'Gestire e consegnare divise', group: 'Divise' },
    [PERMISSIONS.CANTEEN_ORDER_OWN]: { name: 'Ordinare il pranzo in mensa', group: 'Mensa' },
    [PERMISSIONS.CANTEEN_MANAGE]: {
        name: 'Caricare il menù e vedere gli ordini della mensa',
        group: 'Mensa',
    },
    [PERMISSIONS.CANTEEN_BILLING]: {
        name: 'Rendicontare la mensa: listini, prospetto del mese, storico di una persona',
        group: 'Mensa',
    },
    [PERMISSIONS.CHAT_USE]: { name: 'Usare la chat', group: 'Comunicazione' },
    [PERMISSIONS.CHAT_MODERATE]: { name: 'Moderare la chat', group: 'Comunicazione' },
    [PERMISSIONS.NOTIFICATIONS_READ_OWN]: { name: 'Ricevere notifiche', group: 'Comunicazione' },
    // Le due caselle si leggono in fila nella schermata dei ruoli, e la seconda dice da sé che
    // è un allargamento della prima: «anche» non è una parola di stile, è quel che impedisce di
    // spuntare la seconda credendo che sostituisca la prima (la porta resta `:team`).
    [PERMISSIONS.NOTIFICATIONS_SEND_TEAM]: {
        name: 'Inviare comunicazioni ai propri sottoposti',
        group: 'Comunicazione',
    },
    [PERMISSIONS.NOTIFICATIONS_SEND_ALL]: {
        name: 'Inviare comunicazioni anche a tutta l’azienda',
        group: 'Comunicazione',
    },
    [PERMISSIONS.CONTACTS_READ]: { name: 'Vedere la rubrica', group: 'Comunicazione' },
    [PERMISSIONS.CONTACTS_MANAGE]: { name: 'Gestire la rubrica', group: 'Comunicazione' },
    [PERMISSIONS.LOGISTICS_LOCATIONS_READ]: { name: 'Vedere i luoghi', group: 'Logistica' },
    [PERMISSIONS.LOGISTICS_LOCATIONS_CREATE]: { name: 'Creare un luogo', group: 'Logistica' },
    [PERMISSIONS.LOGISTICS_LOCATIONS_MANAGE]: {
        name: 'Modificare e disattivare i luoghi',
        group: 'Logistica',
    },
    [PERMISSIONS.LOGISTICS_PACKAGE_TYPES_MANAGE]: { name: 'Gestire i tipi di collo', group: 'Logistica' },
    [PERMISSIONS.LOGISTICS_TRANSFERS_READ]: { name: 'Vedere le spedizioni', group: 'Logistica' },
    [PERMISSIONS.LOGISTICS_TRANSFERS_MANAGE]: { name: 'Gestire le spedizioni', group: 'Logistica' },
    [PERMISSIONS.LOGISTICS_TRIPS_READ]: { name: 'Vedere i viaggi', group: 'Logistica' },
    [PERMISSIONS.LOGISTICS_TRIPS_MANAGE]: { name: 'Pianificare i viaggi', group: 'Logistica' },
    [PERMISSIONS.LOGISTICS_TRIPS_DRIVE]: { name: 'Eseguire i viaggi (autista)', group: 'Logistica' },
    [PERMISSIONS.SETTINGS_READ]: { name: 'Vedere le impostazioni', group: 'Sistema' },
    [PERMISSIONS.SETTINGS_MANAGE]: { name: 'Modificare le impostazioni', group: 'Sistema' },
    [PERMISSIONS.AUDIT_READ]: { name: 'Consultare il registro attività', group: 'Sistema' },
    [PERMISSIONS.AUDIT_READ_AUTH]: {
        name: 'Vedere nel registro anche accessi e tentativi falliti',
        group: 'Sistema',
    },
    [PERMISSIONS.JOBS_READ]: { name: 'Vedere lo stato delle automazioni', group: 'Sistema' },
    [PERMISSIONS.REPORTS_EXPORT]: { name: 'Esportare report', group: 'Sistema' },
    [PERMISSIONS.TELEPHONY_MANAGE]: {
        name: 'Gestire il centralino (Beste Talk): interni, chiamate, telefoni',
        group: 'Comunicazione',
    },
};
/** Everything an ordinary employee can do. Every other role builds on this. */
const EMPLOYEE_BASE = [
    /// Ordinare il pranzo lo può chiunque lavori qui: non è un privilegio di ruolo.
    /// A decidere chi vede la funzione è la **sede** (`canteen_enabled`), non il permesso —
    /// chi non appartiene a una sede abilitata non la vede affatto (`spec/mensa.md` §3 D2).
    /// Il permesso dice «puoi ordinare per te», e resta vero anche quando non c'è niente da
    /// ordinare: così la rotta nega a chi non deve e la sede nasconde a chi non serve, che
    /// sono due difese diverse e non una ripetuta.
    PERMISSIONS.CANTEEN_ORDER_OWN,
    PERMISSIONS.DEPARTMENTS_READ,
    PERMISSIONS.HEADQUARTERS_READ,
    PERMISSIONS.WORKTIMES_READ_OWN,
    PERMISSIONS.WORKTIMES_WRITE_OWN,
    PERMISSIONS.JUSTIFICATIONS_READ_OWN,
    PERMISSIONS.JUSTIFICATIONS_WRITE_OWN,
    PERMISSIONS.PROPOSALS_RESPOND,
    PERMISSIONS.LEAVE_BALANCES_READ_OWN,
    PERMISSIONS.PAYCHECKS_READ_OWN,
    PERMISSIONS.EXPENSES_READ_OWN,
    PERMISSIONS.EXPENSES_WRITE_OWN,
    PERMISSIONS.BUSINESS_TRIPS_READ_OWN,
    PERMISSIONS.BUSINESS_TRIPS_WRITE_OWN,
    PERMISSIONS.VEHICLES_READ,
    PERMISSIONS.VEHICLES_BOOK,
    PERMISSIONS.MEETING_ROOMS_READ,
    PERMISSIONS.MEETING_ROOMS_BOOK,
    PERMISSIONS.CALENDAR_READ_OWN,
    PERMISSIONS.CALENDAR_WRITE,
    PERMISSIONS.UNIFORMS_REQUEST,
    PERMISSIONS.CHAT_USE,
    PERMISSIONS.NOTIFICATIONS_READ_OWN,
    PERMISSIONS.CONTACTS_READ,
    /*
      ⚠ **I tre permessi di D59, decisi da Cosimo il 2026-10-03** — e sono tre decisioni, non
      una comodità di interfaccia.
  
      Il difetto che li ha chiesti: nel foglio del «+» di un dipendente qualunque mancavano «Fissa
      una riunione» e «Prenota un ritiro». Non era una voce dimenticata — `usaCreabili` le
      mostra a chi le può fare, e un dipendente non le poteva fare. La domanda era quindi se
      **debba** poterle fare, ed è una domanda per chi comanda, non per chi programma.
  
      **`USERS_READ`** — «sì, a tutti i dipendenti». Convocare una riunione comincia con lo
      scegliere chi invitare, e senza l'elenco dei colleghi quel primo passo è una schermata
      vuota. Quel che si apre è l'elenco del personale (nome, reparto, sede): `users.service`
      nasconde comunque codice fiscale e data di nascita a chi non è l'ufficio, e buste paga,
      assenze e note spese stanno dietro permessi loro. Quel che **cambia davvero** è che da oggi
      ogni dipendente vede la rubrica aziendale, ed è la conseguenza che Cosimo ha scelto sapendola.
  
      **`LOGISTICS_TRANSFERS_MANAGE` + `LOGISTICS_LOCATIONS_READ`** — «una spedizione/ritiro della
      logistica», chiesto per esteso: «mettigli anche la richiesta divise però». I due vanno
      insieme perché il modulo non si apre senza il secondo — il primo campo è *da dove a dove*.
      ⚠ **Da saperlo**: `manage` non è «chiedi un ritiro», è il permesso della **logistica**, e
      apre anche la modifica delle spedizioni altrui. Oggi è così perché un permesso più stretto
      — «crea la tua, non toccare le altre» — nel catalogo **non esiste**; il giorno che servisse
      si scrive, e questa riga è il posto da cui ripartire.
  
      La «richiesta divise» non è qui perché il permesso `UNIFORMS_REQUEST` c'era già: a mancare
      era il **reparto nel profilo** dopo l'ingresso, ed era un difetto nostro (`publicUser`).
    */
    PERMISSIONS.USERS_READ,
    /*
      ⚠ **La logistica a tutti, e l'eccezione non sta qui.**
  
      Cosimo, 2026-10-03: *«i ritiri e i luoghi devono essere creabili da ogni account di ogni
      persona dal backend, a parte i dipendenti di Gissi»*. La prima metà è questa riga; la
      seconda **non può** stare in un permesso di ruolo, perché chi lavora a Gissi ha lo stesso
      ruolo `employee` di chi lavora qui. L'eccezione è un'**abilitazione per sede**
      (`headquarters.enable_logistics`), la settima della famiglia, e vive in
      `apps/api/src/users/funzioni-attive.ts` insieme alle altre sei.
  
      I quattro vanno insieme e non si possono dividere:
  
        - `TRANSFERS_MANAGE` scrive la spedizione, `TRANSFERS_READ` la fa **vedere** — senza il
          secondo si può creare un ritiro e non ritrovarlo, che è la forma peggiore di «fatto»;
        - `LOCATIONS_CREATE` crea il luogo, `LOCATIONS_READ` lo propone come capo della tratta,
          che è il primo campo del modulo.
  
      ⚠ **Che cosa si apre davvero, scritto perché non si scopra dopo**: `TRANSFERS_MANAGE` non è
      «crea la tua», è il permesso della **logistica**, e comprende la modifica delle spedizioni
      altrui. Un permesso più stretto per le spedizioni nel catalogo non esiste; il giorno che
      servisse si scrive, e questa riga è il posto da cui ripartire.
  
      ⚠⚠ **Per i luoghi invece è già stato scritto, e il 14 ottobre.** Qui c'era
      `LOCATIONS_MANAGE`, dato perché l'autista possa *creare*, e che però portava con sé
      rinominare, fondere, disattivare e riattivare i luoghi di tutti: **445 persone** su **259**
      luoghi. Adesso qui sta `LOCATIONS_CREATE`, che è la domanda vera di questa riga, e `manage`
      resta a chi cura l'anagrafica (`LOGISTICA_EXTRA`, e l'amministratore che ha tutto).
    */
    PERMISSIONS.LOGISTICS_TRANSFERS_MANAGE,
    PERMISSIONS.LOGISTICS_TRANSFERS_READ,
    PERMISSIONS.LOGISTICS_LOCATIONS_CREATE,
    PERMISSIONS.LOGISTICS_LOCATIONS_READ,
];
const MANAGER_EXTRA = [
    PERMISSIONS.USERS_READ,
    PERMISSIONS.WORKTIMES_READ_TEAM,
    PERMISSIONS.JUSTIFICATIONS_READ_TEAM,
    PERMISSIONS.JUSTIFICATIONS_APPROVE_L1,
    /*
      Qui c'era `expenses:review`, e **non c'è più**: è D21, decisa da Cosimo il 2026-09-06 —
      «i rimborsi delle note spese li autorizza solo l'amministrazione, dal Cruscotto; le note
      spese escono dalla firma sull'app: il responsabile non se le trova più fra le cose da
      approvare».
  
      L'app aveva già smesso di mostrarle il 2026-08-31 (`firma.ts`, `spese = false`), ma quella
      era **metà** della decisione e la metà che si vede: il permesso restava, quindi un
      responsabile continuava a trovarsi la scheda «Note spese da rivedere» sul Cruscotto e a
      poter chiamare `POST /expenses/:id/approve` a mano. Una porta chiusa solo dal lato del
      client non è chiusa: è la lezione di K196, e vale qui esattamente come per le circolari.
  
      Quanto costa a chi lavora, misurato sul collaudo e non stimato: **niente**. Il ruolo
      `manager` in banca dati ce l'ha **una persona sola, `collaudo.responsabile` (900985)**, che
      è un account di prova; i sedici responsabili veri sono `employee` e `expenses:review` non
      l'hanno mai avuto — tanto che le 287 note ferme in `submitted` oggi le può decidere
      soltanto chi ha `expenses:manage`, cioè `hr` e `admin`. Togliere il permesso a `manager`
      non sposta una nota di mano a nessuno: toglie una facoltà che nessuna persona viva
      esercitava, e che dal 2026-08-31 il telefono già non offriva più.
  
      **`hr` non lo perde**, e ci vuole una riga apposta: `HR_EXTRA` parte da questo elenco, e
      senza quella riga Risorse Umane si sarebbe ritrovata fuori dalle rotte `/approve` e
      `/reject` — che chiedono `expenses:review` al livello del metodo, dove `expenses:manage`
      non le sostituisce. Sarebbero rimaste le 287 note in coda con i soli `admin` a poterle
      decidere. Vedi `HR_EXTRA` qui sotto.
    */
    PERMISSIONS.CALENDAR_READ_ALL,
    // Il responsabile scrive **ai suoi**, non all'azienda: `_TEAM` e non `_ALL` è tutta la
    // decisione del 2026-09-06 messa in una riga.
    PERMISSIONS.NOTIFICATIONS_SEND_TEAM,
];
/**
 * `ARCHIVE_TERMINATED_READ` sta qui e **non** in `MANAGER_EXTRA`, ed è la sostanza della
 * scelta: un responsabile continua a vedere il nome di chi se n'è andato dove quel nome è
 * già scritto — la timbratura che ha corretto, la nota spese che ha approvato —, ma non può
 * più aprirne la scheda e leggere codice fiscale, data di nascita e recapiti privati. Fino a
 * oggi poteva, perché l'elenco delle anagrafiche mostrava tutti per scelta esplicita.
 */
const HR_EXTRA = [
    // La rubrica dei dipendenti: HR la apre per mestiere. Vedi `USERS_DIRECTORY`.
    PERMISSIONS.USERS_DIRECTORY,
    ...MANAGER_EXTRA,
    /// Il quadro degli ordini del giorno, scelto da Cosimo il 2026-09-14: servirà quando
    /// arriverà il riaddebito in busta paga, che è lavoro loro. Il permesso è lo stesso
    /// della reception perché le schermate sono le stesse; a separare i due mestieri è il
    /// fatto che il menù lo carica solo chi è reception, e quello lo impone la rotta.
    PERMISSIONS.CANTEEN_MANAGE,
    /// E la rendicontazione, che è il lavoro che questo ufficio faceva già: il programmino
    /// `BSTMensa` sulla share lo aprivano loro, per contare i pasti del mese e controllare la
    /// fattura del fornitore. Cosimo, 2026-09-30: «questa funzione deve essere assegnata al
    /// reparto HR». L'ha data anche ai centralini — vedi `RECEPTION_EXTRA`.
    PERMISSIONS.CANTEEN_BILLING,
    PERMISSIONS.ARCHIVE_TERMINATED_READ,
    PERMISSIONS.USERS_WRITE,
    PERMISSIONS.USERS_DEACTIVATE,
    PERMISSIONS.USERS_RESET_PASSWORD,
    PERMISSIONS.DEPARTMENTS_WRITE,
    // I turni di tutti, **in sola visione** (Cosimo, 2026-10-08). Non `SHIFTS_MANAGE_ALL`: la
    // settimana la costruisce chi guida il reparto, e l'ufficio del personale la guarda.
    PERMISSIONS.SHIFTS_READ_ALL,
    // Le linee di produzione le costruisce chi conosce l'organigramma: le assegnazioni
    // cambiano quando qualcuno cambia reparto, e chi lo registra e' questo ufficio.
    PERMISSIONS.PRODUCTION_LINES_MANAGE,
    PERMISSIONS.WORKTIMES_READ_ALL,
    PERMISSIONS.WORKTIMES_CORRECT,
    PERMISSIONS.JUSTIFICATIONS_READ_ALL,
    PERMISSIONS.JUSTIFICATIONS_APPROVE_L2,
    PERMISSIONS.JUSTIFICATIONS_MANAGE,
    PERMISSIONS.PROPOSALS_MANAGE,
    PERMISSIONS.LEAVE_BALANCES_MANAGE,
    PERMISSIONS.PAYCHECKS_MANAGE,
    /*
      Qui c'erano `expenses:review` e `expenses:manage`, e **non ci sono più**. È la coda di D21,
      e la domanda che il 2026-09-06 era stata lasciata aperta apposta — «D21 dice "non Risorse
      Umane", ma quella frase nomina il ruolo nuovo: toglierlo davvero a HR sarebbe una decisione
      a sé» — Cosimo l'ha chiusa il 2026-09-07 con quattro parole: **«No, solo il ruolo nuovo.»**
  
      Quindi i rimborsi li decide `note_spese` e nessun altro pacchetto oltre ad `admin`, che li
      ha per il bypass da superuser e non per una riga scritta qui.
  
      **Perché questa riga non basta, e serve anche una migrazione.** Il seme non ripassa mai su
      un ruolo già nato (P42, «vince il pannello»): su un impianto vivo — il collaudo, la
      produzione domani — `hr` esiste già con le due caselle spuntate, e togliergliele dal codice
      non le toglie dalla banca dati. La revoca su un impianto vivo è il mestiere della
      migrazione `20260907090000_note_spese_solo_al_ruolo_nuovo`, esattamente come la revoca a
      `manager` di ieri. Questa riga governa gli impianti **nuovi**; quella governa quelli in
      piedi. Servono tutte e due, e per la stessa ragione simmetrica per cui ieri ne servivano
      due in senso opposto.
  
      **L'ordine in cui è stato fatto, che è la parte che conta.** Prima il ruolo a una persona
      vera (`davide.lanzilli`, 563 — dal Cruscotto, il 2026-09-07), poi questa revoca. Al
      contrario, per l'intervallo fra i due gesti le 287 note ferme in `submitted` non le avrebbe
      decise più nessuno tranne gli `admin`: `silvia.stefanacci` e `maria.parrini` erano le
      uniche due non-admin a poterlo fare, e sono `hr`.
  
      **Che cosa se ne va con `expenses:manage`, oltre alla decisione.** Anche il catalogo dei
      tipi di spesa (`expense-note-types.controller.ts`, e la voce «Tipi di spesa» del Cruscotto,
      `navigation.ts`), la lettura delle note altrui e la liquidazione sul cedolino. Non è un
      effetto collaterale da correggere: chi decide che cosa si rimborsa è anche chi tiene
      l'elenco di che cosa si può chiedere, ed è la stessa scrivania.
  
      **Che cosa HR conserva**, perché non si legga questa riga come un ridimensionamento più
      largo di quel che è: `business-trips:manage` resta qui sotto — le trasferte sono di
      Risorse Umane e non si sono mai spostate — e restano `expenses:read:own` ed
      `expenses:write:own` da `EMPLOYEE_BASE`: chi lavora in HR presenta le proprie note spese
      come tutti gli altri, e adesso è un'altra scrivania a decidergliele.
    */
    PERMISSIONS.BUSINESS_TRIPS_MANAGE,
    PERMISSIONS.UNIFORMS_MANAGE,
    PERMISSIONS.CONTACTS_MANAGE,
    // La circolare a tutta l'azienda. Sta qui e in `admin` e in nessun altro pacchetto: è la
    // metà «HR e Amministratori a tutti» della decisione. `_TEAM` arriva da `MANAGER_EXTRA`
    // qui sopra, e deve continuare ad arrivarci: `_ALL` allarga una porta, non ne apre una.
    PERMISSIONS.NOTIFICATIONS_SEND_ALL,
    PERMISSIONS.REPORTS_EXPORT,
    // Il registro sì; le righe di accesso no. `AUDIT_READ_AUTH` manca qui **per scelta**, ed
    // è l'unico posto in cui quella scelta è visibile: chi aggiunge un permesso a questo
    // elenco lo fa scorrendolo, e una riga che non c'è non si nota. Sta scritto qui perché
    // il giorno in cui qualcuno la aggiungerà sappia che sta disfando qualcosa (sweep R4b).
    PERMISSIONS.AUDIT_READ,
];
/**
 * Logistica — un solo account (Cosimo 2026-08-29): l'unione del vecchio «gestore viaggi»
 * (anagrafiche: luoghi, mezzi, tipi di collo) e del vecchio «account responsabile» (operativo:
 * pianifica viaggi, gestisce spedizioni, avvisa). Chi cura le anagrafiche e chi pianifica sono la
 * stessa persona, quindi un ruolo solo con tutti e due i mestieri.
 */
const LOGISTICA_EXTRA = [
    // anagrafiche (ex gestore viaggi)
    PERMISSIONS.LOGISTICS_LOCATIONS_READ,
    // `create` si ripete qui e non si deduce da `manage`: la rotta che crea chiede `create`, e
    // un ruolo che avesse solo `manage` potrebbe rinominare un luogo e non farne uno nuovo.
    PERMISSIONS.LOGISTICS_LOCATIONS_CREATE,
    PERMISSIONS.LOGISTICS_LOCATIONS_MANAGE,
    PERMISSIONS.LOGISTICS_PACKAGE_TYPES_MANAGE,
    PERMISSIONS.VEHICLES_MANAGE,
    // operativo (ex account responsabile)
    PERMISSIONS.LOGISTICS_TRANSFERS_READ,
    PERMISSIONS.LOGISTICS_TRANSFERS_MANAGE,
    PERMISSIONS.LOGISTICS_TRIPS_READ,
    PERMISSIONS.LOGISTICS_TRIPS_MANAGE,
    /*
      Qui c'era `notifications:send`, e **non c'è più**: Logistica non manda circolari affatto.
      Deciso da Cosimo il 2026-09-06, dopo aver chiesto di guardare prima i dati — «se non lo
      usa, è una porta aperta che non serve a nessuno». Guardati, e non lo usa:
  
       · in 22.214 notifiche sul collaudo, **nessuna** — di nessun tipo — porta come autore uno
         dei due account `logistica` (`Gestore Viaggi` 708, `Account Responsabile` 709). Le
         uniche con un autore scritto sono 113, tutte di account `admin`. Nel registro attività
         le uniche righe `notification/create` sono nove, anch'esse di un `admin`;
       · l'«avvisa» per cui il permesso gli fu dato il 2026-08-29 — l'autista che deve sapere del
         viaggio, la spedizione urgente — **non passa di qui**: quelle notifiche le scrivono i
         servizi con `tryNotify` e i destinatari già in mano, senza consultare nessun permesso.
         Togliere `send` a Logistica non spegne un solo avviso operativo, e il ruolo resta quello
         che era: pianifica, gestisce, avvisa;
       · restava il dubbio se dargli `_TEAM` come a un responsabile. Non avrebbe voluto dire
         niente: i due account non hanno reparto, non dirigono nessuno e non sono approvatori di
         nessuno, quindi «i miei» per loro è l'insieme vuoto. Un permesso che rifiuta sempre è
         peggio di un permesso che non c'è — chi lo spunta crede di aver delegato qualcosa.
  
      Un avvertimento per chi legge fra sei mesi: le 22.100 notifiche senza autore sono un buco
      del **passaggio dati** (il vecchio aveva `creator_id/sender_id` quasi sempre nullo), non
      una prova che nessuno le abbia scritte. Non intacca la conclusione — il ruolo `logistica`
      nel vecchio sistema non esisteva affatto, lì 496 utenti su 505 avevano il ruolo nullo — ma
      va detto, perché un numero riportato senza il suo limite diventa un'altra cosa.
    */
    /*
      E qui c'era `reports:export`, tolto lo stesso giorno e per mano dello stesso giro: Cosimo
      l'ha voluto ad **Amministratore e Risorse Umane** e basta — sette persone attive prima,
      cinque dopo (3 `admin`, 2 `hr`, 2 `logistica`, contate sul collaudo).
  
      Una correzione, perché la decisione è stata presa con accanto una nota vecchia che diceva
      «tanto è un permesso morto, non lo consulta nessuna rotta»: **non lo è più**. La deroga in
      `check-permessi.mjs` è stata tolta il 2026-08-29 proprio perché ha smesso di esserlo, e oggi
      cinque rotte lo pretendono — l'esportazione CSV di anagrafiche, reparti, assenze, note spese
      e trasferte. Toglierlo a Logistica quindi **non è un gesto senza effetto**: da questo
      rilascio quei due account quelle cinque esportazioni non le fanno più. Che nessuno se ne
      accorga è probabile ma per un'altra ragione, misurata e non sperata: nel registro attività
      del collaudo le righe `export` sono **zero**, di chiunque. La decisione resta quella di
      Cosimo; a essere sbagliata era la ragione che gli era stata messa accanto, e le decisioni
      prese su una ragione sbagliata sono quelle che qualcuno disfa in buona fede sei mesi dopo.
    */
];
const DRIVER_EXTRA = [
    PERMISSIONS.LOGISTICS_LOCATIONS_READ,
    PERMISSIONS.LOGISTICS_TRIPS_READ,
    PERMISSIONS.LOGISTICS_TRIPS_DRIVE,
    PERMISSIONS.LOGISTICS_TRANSFERS_READ,
];
/**
 * Reception — front-desk di una sede. Solo letture, e **ristrette alla propria sede** dallo
 * scope (`src/auth/site-scope.ts`): il permesso apre la porta a sale e mezzi, la sede stringe a
 * quelli del proprio stabilimento. Si parte da questi due — gli unici oggi scopati in modo
 * pulito (`headquarter_id` sulla riga) — e si allarga (agenda visite, calendario, consegna
 * divise) a mano a mano che ogni dominio viene scopato: aggiungere qui un permesso la cui
 * lettura non è ancora scopata farebbe vedere alla reception le altre sedi.
 */
const RECEPTION_EXTRA = [
    /// La mensa è lavoro suo: carica il menù della settimana, accende le sedi, guarda il
    /// quadro del giorno e riceve la mail delle 09:45 da inoltrare al fornitore.
    PERMISSIONS.CANTEEN_MANAGE,
    /// E ordina anche per sé: il banco reception non sta in `EMPLOYEE_BASE` (è un banco, non
    /// una persona), ma dietro quel banco ci sta qualcuno che pranza.
    PERMISSIONS.CANTEEN_ORDER_OWN,
    /// La rendicontazione: listini, prospetto del mese, storico di una persona.
    ///
    /// **Deciso da Cosimo il 2026-09-30**, in due messaggi: «questa funzione deve essere assegnata
    /// al reparto HR» e, subito dopo, «e agli account dei centralini». Io avevo proposto di darla
    /// solo a Risorse Umane; la decisione è sua e sta qui col suo perché, che è semplice: quel
    /// programmino sulla share lo apriva anche il banco.
    ///
    /// ⚠ **È l'eccezione al monito qui sopra, e va saputa invece che scoperta.** Il prospetto
    /// **non è scopato per sede**: la reception di Colle vede il conto di tutta l'azienda, nome per
    /// nome, non solo dei propri. Non è una dimenticanza — è la conseguenza di questa decisione, e
    /// se un giorno si volesse stringere basta filtrare per `headquarter_id` dell'utenza nel
    /// servizio, che il filtro per sede ce l'ha già. Che si debba stringere è una domanda per
    /// Cosimo, non una cosa da fare di nascosto: sta in `spec/mensa.md` §10.
    PERMISSIONS.CANTEEN_BILLING,
    PERMISSIONS.MEETING_ROOMS_READ,
    // Prenotare le sale del proprio stabilimento: `assertRoomBookable` in `calendar.service`
    // controlla la sede prima di lasciar prenotare, così il permesso non apre le sale altrui.
    PERMISSIONS.MEETING_ROOMS_BOOK,
    PERMISSIONS.CALENDAR_WRITE,
    // L'anagrafica del personale della propria sede: `users.service.list`/`get` applicano lo
    // scope-sede (e nascondono codice fiscale/nascita alla reception). Sicuro solo perché quei
    // due sono scopati — vedi il monito qui sopra.
    PERMISSIONS.USERS_READ,
    // E la rubrica: la reception la usa da agosto, col recinto per sede.
    PERMISSIONS.USERS_DIRECTORY,
    PERMISSIONS.VEHICLES_READ,
    // L'agenda del banco: gli appuntamenti nelle sale della sua sede. È `read:own` e non
    // `read:all` di proposito — lo scope-sede in `calendar.service` (`visibilityWhere`, `mayRead`,
    // `availability`) fa vedere alla reception gli eventi delle **sale del suo stabilimento**, e
    // `read:own` la fa entrare senza aprirle la porta a «tutti gli eventi» che `read:all` aprirebbe.
    PERMISSIONS.CALENDAR_READ_OWN,
    // Le spedizioni e i ritiri del proprio stabilimento: `transfers.service` scopa `list`/`find`
    // alla sede (un luogo con l'`headquarterId` della reception). Sola lettura — la portineria
    // vede cosa entra ed esce, non muove le righe (quello resta `LOGISTICS_TRANSFERS_MANAGE`).
    PERMISSIONS.LOGISTICS_TRANSFERS_READ,
    // Nessun `NOTIFICATIONS_SEND_*`, e vale la pena dire che è stato **guardato** e non
    // dimenticato (2026-09-06): la reception non ha mai avuto `notifications:send` — né qui né
    // in banca dati sul collaudo, dove il permesso vecchio risultava concesso ad `admin`, `hr`,
    // `logistica` e `manager` e a nessun altro. Se un giorno lo si vorrà, lo scope suo è la
    // **sede** (`site-scope.ts`), non il reparto: `_TEAM` le darebbe l'insieme vuoto, perché una
    // reception non è capo di nessuno. Servirebbe una platea per sede scritta apposta, e allora
    // sarà una decisione, non una spunta.
];
/**
 * Note spese — l'amministrazione che **autorizza e liquida i rimborsi** (D21, Cosimo
 * 2026-09-06). Due caselle, e sono le due metà dello stesso mestiere: `:review` decide quanto
 * è dovuto, `:manage` registra il pagamento sul cedolino. Il controller le tiene separate
 * apposta — «approving and paying are two different rights on purpose» — e qui stanno insieme
 * perché a Beste le fa la stessa scrivania.
 *
 * **Perché questo ruolo parte da `EMPLOYEE_BASE` e `reception`/`magazzino_divise` no.**
 * È la domanda che decide tutto, e la risposta non è di gusto: sta in banca dati.
 *
 * `User.roleCode` è **una colonna sola**. Non esistono ruoli secondari: chi riceve questo
 * ruolo non ne ha nessun altro, e quello che il pacchetto non gli dà non glielo dà nessuno.
 * Quindi la domanda vera è «a chi lo si darà?», e sul collaudo si sa: il reparto
 * AMMINISTRAZIONE ha **otto persone attive** — sette `employee` e `silvia.stefanacci` che è
 * già `hr` — tutte con matricola, badge, data di assunzione e reparto. Sono dipendenti, e
 * `enable_expense_notes` ce l'hanno come gli altri 492.
 *
 * Se questo fosse un banco, il giorno in cui Cosimo assegna il ruolo a una di loro quella
 * persona **perde la propria vita da dipendente**: non timbra più, non chiede ferie, non vede
 * la propria busta paga né il proprio residuo, non prenota una sala, non ha la rubrica né la
 * chat, e non può presentare una nota spese sua. Nessuno lo avrebbe chiesto e nessuno se ne
 * accorgerebbe leggendo questo file: si scoprirebbe dal telefono di lei, lunedì mattina.
 *
 * La prova al contrario è nei due banchi veri, e vale la pena guardarla perché è la stessa
 * misura fatta sui casi opposti: `reception` e `magazzino_divise` hanno in tutto **un account
 * ciascuno** (900969, 900970), e tutti e due **senza matricola, senza badge, senza reparto e
 * senza data di assunzione**. Sono accessi appesi a un bancone, non persone. Il precedente
 * giusto per «Note Spese» non è quello: è `hr`, che è l'altro ufficio fatto di dipendenti veri
 * — `silvia.stefanacci` (matricola 195, in azienda dal 2006) e `maria.parrini` (602, dal 2023)
 * — e che infatti parte da `EMPLOYEE_BASE`.
 *
 * **`EMPLOYEE_BASE` non allarga la porta sui soldi degli altri**: dà letture `:own` e
 * prenotazioni, niente che riguardi le pratiche altrui. E la separazione fra chi presenta e chi
 * decide non la fa il ruolo, la fa la riga: `assertMayReview` rifiuta l'auto-revisione con un
 * messaggio esplicito, e il vincolo in banca dati impedisce che `reviewed_by` sia il titolare
 * della nota. Dare a questa persona la sua vita da dipendente **non le permette di approvarsi
 * una spesa**. Resta vero che una nota **approvata da un altro** la può poi segnare liquidata
 * lei: il pagamento non è mai il primo atto, quindi due mani ci sono sempre state. Fino al
 * 2026-09-07 questo si diceva «come già oggi per HR»; adesso non si può più, perché HR le due
 * caselle non le ha più (`HR_EXTRA`, decisione di Cosimo «solo il ruolo nuovo»), e questo è
 * l'unico pacchetto non-`admin` che le porta.
 *
 * **Che cosa non c'è, e non per dimenticanza.** Niente `business-trips:manage`: le trasferte
 * restano di HR, e la schermata web delle note spese non le chiede — il viaggio arriva già
 * dentro la risposta (`DETAIL_INCLUDE`), quindi chi decide la nota vede la trasferta senza
 * avere il diritto di riscriverla. Niente `users:read`: nome e matricola del richiedente
 * viaggiano nella stessa risposta, e quello è il motivo per cui `NoteSpeseView.vue` non chiama
 * mai `/users` — la trappola in cui erano cascate le circolari (una tendina che cercava su una
 * rotta vietata e restituiva il vuoto, che si legge «quella persona non esiste») qui non c'è.
 * Niente `reports:export`: l'esportazione CSV delle note spese la protegge `reports:export`,
 * che Cosimo il 2026-09-06 ha voluto ad Amministratore e Risorse Umane **e basta**; darlo qui
 * sarebbe rifare in silenzio una decisione di dieci giorni prima.
 */
const NOTE_SPESE_EXTRA = [
    PERMISSIONS.EXPENSES_REVIEW,
    PERMISSIONS.EXPENSES_MANAGE,
];
const dedupe = (codes) => [...new Set(codes)];
/** `admin` is intentionally absent: it is granted every permission by the seed. */
/**
 * Quel che l'ufficio del personale **non** ha, pur stando su `EMPLOYEE_BASE`.
 *
 * Richiesto da Cosimo il 2026-10-08, guardando il suo Cruscotto: «togli la gestione divise e
 * divise per reparto», «togli la parte di logistica», «togli la parte di agenda». Quelle voci
 * non arrivavano da `HR_EXTRA`: arrivano da `EMPLOYEE_BASE`, che e' di **tutti** i ruoli —
 * quindi non si potevano togliere di la' senza toglierle anche a un autista. Si sottraggono
 * qui, per questo ruolo solo, e la sottrazione e' **scritta** invece che dedotta.
 *
 * ⚠ **Che cosa NON c'e' in questa lista, e perche'.** I permessi stanno sul **ruolo**, non
 * sull'account: `hr` non e' solo il banco `personale`, e' anche una **persona vera** con
 * **167 riunioni** in calendario. Quindi se ne vanno le viste d'**insieme** — la gestione
 * divise, la logistica, le riunioni di tutti — e restano le cose che una persona usa per se':
 * il proprio calendario (`calendar:read:own`, `calendar:write`), prenotare una sala o un mezzo
 * (`meeting-rooms:*`, `vehicles:*` — e li' il `:read` serve **anche per prenotare**, perche' i
 * due controller lo pretendono in testa), e chiedere una divisa (`uniforms:request`).
 *
 * Perche' l'Agenda non sparisca **del tutto** dal banco servirebbe un **ruolo suo**, separato
 * da quello delle persone dell'ufficio: e' una decisione, non una riga, e sta in
 * `da-decidere.md`.
 */
/**
 * Quel che il banco delle **note spese** non ha, pur stando su `EMPLOYEE_BASE`.
 *
 * Cosimo, 2026-10-08: «dall'account delle note spese togli le spedizioni». Arrivavano dalla
 * migrazione del 3 ottobre che diede la logistica a tutti i ruoli costruiti su
 * `EMPLOYEE_BASE`; questo banco controlla rimborsi, non pacchi.
 *
 * ⚠ A differenza di `HR_SENZA` qui non c'e' niente da salvare: non si toglie nessuna
 * capacita' che una persona usi **per se'** — ferie, buste paga, chat e le proprie note
 * spese restano, e vengono da `EMPLOYEE_BASE`.
 */
const NOTE_SPESE_SENZA = new Set([
    PERMISSIONS.LOGISTICS_TRANSFERS_READ,
    PERMISSIONS.LOGISTICS_TRANSFERS_MANAGE,
    PERMISSIONS.LOGISTICS_LOCATIONS_READ,
    PERMISSIONS.LOGISTICS_LOCATIONS_CREATE,
    PERMISSIONS.LOGISTICS_LOCATIONS_MANAGE,
]);
const HR_SENZA = new Set([
    // «Togli la gestione divise e divise per reparto»: tutt'e due le voci si accendono con
    // questo, e nient'altro lo usa per HR.
    PERMISSIONS.UNIFORMS_MANAGE,
    // «Togli la parte di logistica»: spedizioni, viaggi, luoghi. Dati a tutti il 2026-10-03
    // (`20261003090000_logistica_a_tutti`); qui si rimettono fuori per questo ruolo.
    PERMISSIONS.LOGISTICS_TRANSFERS_READ,
    PERMISSIONS.LOGISTICS_TRANSFERS_MANAGE,
    PERMISSIONS.LOGISTICS_LOCATIONS_READ,
    PERMISSIONS.LOGISTICS_LOCATIONS_CREATE,
    PERMISSIONS.LOGISTICS_LOCATIONS_MANAGE,
    // «Togli la parte di agenda», per la meta' che si puo': le riunioni **di tutti**. Il
    // calendario proprio resta, se no si acceca chi ce l'ha pieno.
    PERMISSIONS.CALENDAR_READ_ALL,
]);
export const ROLE_PERMISSIONS = {
    [RoleCode.HR]: dedupe([...EMPLOYEE_BASE, ...HR_EXTRA]).filter((c) => !HR_SENZA.has(c)),
    [RoleCode.MANAGER]: dedupe([...EMPLOYEE_BASE, ...MANAGER_EXTRA]),
    [RoleCode.EMPLOYEE]: dedupe(EMPLOYEE_BASE),
    [RoleCode.DRIVER]: dedupe([...EMPLOYEE_BASE, ...DRIVER_EXTRA]),
    [RoleCode.LOGISTICA]: dedupe([...EMPLOYEE_BASE, ...LOGISTICA_EXTRA]),
    // Il banco delle note spese controlla rimborsi, non pacchi: la logistica che `EMPLOYEE_BASE`
    // gli dava se ne va (Cosimo, 2026-10-08). Stessa forma di `HR_SENZA`, con una lista sua.
    [RoleCode.NOTE_SPESE]: dedupe([...EMPLOYEE_BASE, ...NOTE_SPESE_EXTRA]).filter((c) => !NOTE_SPESE_SENZA.has(c)),
    // NON su `EMPLOYEE_BASE`: è un banco, non una persona — non chiede ferie né note spese per sé.
    // Solo i suoi due domini di sede, e null'altro finché non se ne scopa un terzo.
    [RoleCode.RECEPTION]: dedupe(RECEPTION_EXTRA),
    // Il banco divise: gestisce le richieste di vestiario (approva/rifiuta/consegna) e nient'altro.
    // Account dedicato, di magazzino. `uniforms:manage` copre backlog/consegna/approvazione (le
    // rotte con guardia di metodo); `uniforms:request` gli fa passare la guardia di **classe** del
    // controller richieste, così vede anche il dettaglio di una singola richiesta prima di consegnarla.
    [RoleCode.MAGAZZINO_DIVISE]: dedupe([PERMISSIONS.UNIFORMS_MANAGE, PERMISSIONS.UNIFORMS_REQUEST]),
    // SU `EMPLOYEE_BASE`, al contrario dei due qui sopra: è una persona dell'amministrazione, non
    // un bancone. Il perché per esteso — con i numeri su cui è stato deciso — sta su
    // `NOTE_SPESE_EXTRA`. `EMPLOYEE_BASE` porta anche `expenses:read:own`, che non è un di più:
    // `ExpensesController` lo chiede a livello di **classe**, quindi senza quello la schermata
    // web delle note spese prenderebbe 403 sull'elenco pur avendo il diritto di approvare.
};
/**
 * I permessi che **non stanno sul ruolo, stanno sulla funzione**: li ha chi ha qualcuno sotto
 * di sé, per il tempo in cui ce l'ha, qualunque ruolo porti scritto in anagrafica.
 *
 * **Perché esiste questo elenco.** Il 2026-09-06 le circolari hanno preso lo scope e `_TEAM` è
 * finito nel pacchetto del ruolo `manager`. Il giorno dopo si è visto che quel ruolo **in banca
 * dati non ce l'ha nessuno**: i sedici responsabili veri — 14 capi e 2 vice — sono `employee`, e
 * il diritto di dirigere ce l'hanno su `department_role`. «Ogni Responsabile scrive ai suoi» era
 * vero nel codice e non raggiungeva nessuna persona viva.
 *
 * **Perché non si è spostato nessuno di ruolo** (decisione di Cosimo, 2026-09-07): `manager` non
 * porta solo la circolare, porta `users:read`, `worktimes:read:team`, l'approvazione di 1°
 * livello, `expenses:review` e `calendar:read:all`. Promuovere sedici persone per dar loro il
 * diritto di scrivere ai propri vuol dire dargliene altri cinque che nessuno ha chiesto.
 *
 * **Perché non è una riga in banca dati per quelle sedici persone.** Una concessione nominale
 * invecchia il giorno dopo: chi smette di dirigere se la porta dietro, chi comincia non ce l'ha
 * finché qualcuno non se ne ricorda, e nessuno se ne ricorda. Questo elenco invece si risolve
 * **al momento della richiesta**, sullo stesso `subjectsOf` che riempie la coda delle firme —
 * la stessa scelta, e per la stessa ragione, di `ApprovalChainService`, che la catena la risolve
 * al momento della firma e non la congela sulla riga. Un cambio di mansione vale dalla richiesta
 * successiva, in tutti e due i versi, senza migrazioni e senza che nessuno debba accorgersene.
 *
 * **Chi è «chi ha sottoposti»**: `subjectsOf` non vuoto, cioè esattamente `hasSubjects` di
 * `/auth/me` — i membri dei reparti che si dirige (capo o vice, discendenti compresi) più chi ha
 * messo questa persona come approvatore. Una **seconda** definizione di «i miei» è quella che un
 * giorno diverge dalla prima, e allora il menù dice una cosa e la coda un'altra: qui non ce n'è
 * una seconda. E il confine coincide con la platea che `resolveAudience` concede, che è pure
 * lei `subjectsOf`: chi passa la porta può scrivere a qualcuno, sempre.
 *
 * **Perché ci sta solo `_TEAM`.** È il solo diritto che «dirigere» giustifica da sé, senza
 * decidere niente d'altro: scrivere a chi già si firma le ferie. Chi aggiunge una riga qui sta
 * dando quel diritto a quindici persone in un colpo, senza che nessuno spunti niente in nessuna
 * schermata — che si legga come una promozione silenziosa, perché è quello che è.
 *
 * @see apps/api/src/auth/guards/permissions.guard.ts — dove la concessione si applica
 * @see apps/api/src/auth/auth.service.ts — dove finisce nei `permissions` di `/auth/me`
 */
export const PERMESSI_DI_CHI_HA_SOTTOPOSTI = [
    PERMISSIONS.NOTIFICATIONS_SEND_TEAM,
    /*
      ⚠ **Le timbrature dei propri sottoposti** (Cosimo, 2026-10-03: *«deve vederle solo il
      responsabile approvatore dei suoi sottoposti e da backend»*).
  
      Sta **qui** e non in `MANAGER_EXTRA` per la ragione già misurata e scritta qui sopra: il ruolo
      `manager` in banca dati **non ce l'ha nessuno**, i responsabili veri sono `employee` con un
      ruolo di reparto. Metterlo sul ruolo avrebbe scritto una regola giusta che non raggiunge
      nessuna persona viva — ed è esattamente l'errore già fatto una volta con le circolari.
  
      E «i suoi sottoposti» qui vuol dire `subjectsOf`, cioè la **catena di approvazione** e non il
      reparto: la stessa definizione che usa la coda delle firme. Una seconda definizione di «i
      miei» diverge il giorno in cui qualcuno la tocca.
    */
    PERMISSIONS.WORKTIMES_READ_TEAM,
];
/**
 * I diritti di chi è segnato **Autista** sulla scheda (`users.is_driver`).
 *
 * ## Perché esiste, misurato
 *
 * Il 2026-10-13 Cosimo ha provato a creare un viaggio e il server ha risposto ««DE BIASI
 * FRANCESCO» non è abilitato a guidare i viaggi: prima serve il permesso di autista». De Biasi
 * **è** un autista: ce l'ha la spunta. Misurato subito dopo sulla produzione: **sette persone**
 * hanno quella spunta, tutte e sette di ruolo `employee`, e **nessuna** ha un ruolo che conceda
 * `logistics:trips:drive` — il ruolo `driver` non ce l'ha nessuno. Cioè **nessun viaggio poteva
 * essere assegnato a nessuno**, e la tendina degli autisti offriva sette nomi che il server
 * avrebbe rifiutato tutti.
 *
 * ## È lo stesso errore di `PERMESSI_DI_CHI_HA_SOTTOPOSTI`, la terza volta
 *
 * Venti righe più su c'è scritto per esteso: un diritto messo sul **ruolo** quando la cosa che
 * lo giustifica è una **proprietà della persona** scrive una regola giusta che non raggiunge
 * nessuno. È successo con le circolari, poi con le timbrature dei sottoposti, e qui con gli
 * autisti. La forma della cura è la stessa, ed è il motivo per cui questo elenco sta **accanto**
 * a quello: chi arriva a leggerne uno trova l'altro.
 *
 * ## Perché due permessi e non uno
 *
 * `TRIPS_DRIVE` apre le sei rotte del guidare; `TRIPS_READ` apre l'elenco dei propri viaggi.
 * Senza il secondo, un autista potrebbe timbrare le tappe di un viaggio che non riesce ad
 * aprire — cioè un permesso che esiste solo per chi sa l'indirizzo della rotta.
 */
export const PERMESSI_DI_CHI_GUIDA = [
    PERMISSIONS.LOGISTICS_TRIPS_DRIVE,
    PERMISSIONS.LOGISTICS_TRIPS_READ,
];
/**
 * I diritti che dà l'interruttore «Manda comunicazioni aziendali» sull'account
 * (`User.canSendCommunications`, Cosimo 2026-10-30: *«dammi la possibilità di attivare la facoltà
 * di inviare le comunicazioni aziendali anche ai singoli utenti del sistema con un toggle
 * sull'account»*). Sono le due caselle della pagina Comunicazioni: entrarci (`_TEAM`) e scrivere
 * a tutta l'azienda (`_ALL`). Stessa forma di `PERMESSI_DI_CHI_GUIDA`: il diritto arriva dalla
 * persona, il ruolo resta com'è.
 */
export const PERMESSI_DI_CHI_COMUNICA = [
    PERMISSIONS.NOTIFICATIONS_SEND_TEAM,
    PERMISSIONS.NOTIFICATIONS_SEND_ALL,
];
/**
 * I diritti che da' l'interruttore «Gestisce il centralino» sull'account
 * (`User.canManageTelephony`, Cosimo 2026-11-02: *«devo poter dare l'accesso anche a delle persone
 * specifiche»*). Stessa forma di `PERMESSI_DI_CHI_COMUNICA`: il diritto arriva dalla persona.
 */
export const PERMESSI_DI_CHI_GESTISCE_IL_CENTRALINO = [
    PERMISSIONS.TELEPHONY_MANAGE,
];
export const ROLE_LABELS = {
    [RoleCode.ADMIN]: 'Amministratore',
    [RoleCode.HR]: 'Risorse Umane',
    [RoleCode.MANAGER]: 'Responsabile',
    [RoleCode.EMPLOYEE]: 'Dipendente',
    [RoleCode.DRIVER]: 'Autista',
    [RoleCode.LOGISTICA]: 'Logistica (viaggi e spedizioni)',
    [RoleCode.RECEPTION]: 'Reception (banco sede)',
    [RoleCode.MAGAZZINO_DIVISE]: 'Magazzino divise (consegna)',
    [RoleCode.NOTE_SPESE]: 'Note spese (amministrazione)',
};
/**
 * Le etichette dei ruoli di **sistema** in inglese e cinese. L'italiano non è qui: è già
 * `roleName`, che il server risolve — da `ROLE_LABELS` per i ruoli di sistema, dal nome scelto
 * dall'azienda per i ruoli creati dall'utente. Per quelli d'azienda non c'è (e non deve esserci)
 * una traduzione cablata: `nomeRuolo` ci ripiega sul nome del server, così non compare mai un
 * codice grezzo a chi quel ruolo l'ha creato apposta (vedi il commento su `AuthUser.roleName`).
 */
const ROLE_LABELS_EN = {
    [RoleCode.ADMIN]: 'Administrator',
    [RoleCode.HR]: 'HR',
    [RoleCode.MANAGER]: 'Manager',
    [RoleCode.EMPLOYEE]: 'Employee',
    [RoleCode.DRIVER]: 'Driver',
    [RoleCode.LOGISTICA]: 'Logistics (trips & shipments)',
    [RoleCode.RECEPTION]: 'Reception (site desk)',
    [RoleCode.MAGAZZINO_DIVISE]: 'Uniform warehouse (issuing)',
    // Proposti, come gli altri sei: D3 dice che i nomi in inglese e cinese li propone chi
    // costruisce e li corregge Cosimo. Finché non li corregge, chi ha l'app in cinese legge
    // questo e non un codice grezzo — che è tutto quel che questa tabella deve garantire.
    [RoleCode.NOTE_SPESE]: 'Expense claims (administration)',
};
const ROLE_LABELS_ZH = {
    [RoleCode.ADMIN]: '管理员',
    [RoleCode.HR]: '人力资源',
    [RoleCode.MANAGER]: '主管',
    [RoleCode.EMPLOYEE]: '员工',
    [RoleCode.DRIVER]: '司机',
    [RoleCode.LOGISTICA]: '物流（行程与运输）',
    [RoleCode.RECEPTION]: '前台（门岗）',
    [RoleCode.MAGAZZINO_DIVISE]: '工作服仓库（发放）',
    [RoleCode.NOTE_SPESE]: '费用报销（行政）',
};
/**
 * Il nome del ruolo nella lingua di chi legge. In italiano è `roleName` così com'è (il server lo
 * tiene già in italiano). In inglese e cinese si traducono i soli ruoli di sistema; per un ruolo
 * d'azienda si ripiega su `roleName` — mai un codice grezzo.
 */
export function nomeRuolo(roleCode, roleName, lingua) {
    if (lingua === 'en')
        return ROLE_LABELS_EN[roleCode] ?? roleName;
    if (lingua === 'zh')
        return ROLE_LABELS_ZH[roleCode] ?? roleName;
    return roleName;
}
//# sourceMappingURL=permissions.js.map